import { useEffect, useState } from "react"
import type { LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import { LibraryNodeEmpty, LibraryNodeError, LibraryNodeLoading } from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import { fetchLibrarySketches, type LibrarySketchItem } from "@/lib/library-api"
import { libraryKindFromMediaType, projectSketchSource } from "@/lib/library-source"
import type { LibraryViewMode } from "@/lib/library-view-mode"

type LibrarySketchesNodeProps = {
  projectId: number
  mode: LibraryViewMode
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
}

const NO_ITEMS: LibraryItem[] = []

function sketchToLibraryItem(sketch: LibrarySketchItem): LibraryItem {
  const kind = libraryKindFromMediaType(sketch.media_type)
  const isVideo = kind === "video"
  const durationSeconds = sketch.duration_seconds
  return {
    key: `sketch-${sketch.signed_id}`,
    kind,
    label: sketch.label,
    createdAt: sketch.created_at,
    thumbUrl: sketch.thumb_url ?? (kind === "image" ? sketch.url : null),
    source: sketch.id != null ? projectSketchSource(sketch.id) : undefined,
    asset: {
      title: sketch.label,
      kind,
      url: isVideo ? null : sketch.url,
      videoUrl: isVideo ? sketch.url : null,
      downloadUrl: sketch.download_url ?? sketch.url,
      createdAt: sketch.created_at,
      ...(typeof durationSeconds === "number" && Number.isFinite(durationSeconds) && durationSeconds > 0
        ? { durationMs: durationSeconds * 1000 }
        : {}),
    },
  }
}

/** Плоская секция «Эскизы»: Project#sketch_library_files. */
export function LibrarySketchesNode({
  projectId,
  mode,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
}: LibrarySketchesNodeProps) {
  const { t } = useTranslation(["workspace", "common"])
  const [items, setItems] = useState<LibraryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setItems([])
    setLoading(true)
    setLoadFailed(false)
    fetchLibrarySketches(projectId, { signal: controller.signal })
      .then((sketches) => {
        setItems(sketches.map(sketchToLibraryItem))
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadFailed(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [projectId, reloadKey])

  if (loading || loadFailed) {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {loading ? <LibraryNodeLoading /> : <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} />}
      </>
    )
  }

  if (items.length === 0) {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        <LibraryNodeEmpty>{t("library.sketches.empty")}</LibraryNodeEmpty>
      </>
    )
  }

  return (
    <>
      <LibraryItemsReporter items={items} onItemsChange={onItemsChange} />
      <LibraryItems
        items={items}
        mode={mode}
        listLayout="table"
        aspectClass="aspect-square"
        focusedKey={focusedKey}
        onFocusItem={onFocusItem}
        onOpenItem={onOpenItem}
      />
    </>
  )
}
